import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles } from '@material-ui/core';
import { Link } from '@klp/design/Link';
import { Heading, Block, ActionButton, Typography } from '@klp/design';
import type { IContractToRegistrationProcessActivity } from '../../../classes/ContractToRegistrationProcessResponse';
import type { RegistrationStatuses } from '../../../services/rtkApi';
import {
    Activities,
    ConsultantRoles,
    useGetContractToRegistrationQuery,
    useGetDirectLinkQuery,
} from '../../../services/rtkApi';
import {
    getDynamics365URL,
    handleRequiredFormFields,
    useMapUserRolesToCandidateGroup,
} from '../../../utils';
import { getRequiredConfig } from '@klpkonsern/appconfig';
import { generateRouteParams } from '@klpkonsern/routing';
import { ConsultantSelector } from '../../ConsultantSelector';
import type { ILevendeKontrakterParams } from '@klp/levendekontrakter/views/LevendeKontrakter';
import { FormContainerKontraktsforvaltning } from '@klp/formgenerator/index';
import { useSelector } from 'react-redux';
import { contractregistrationSelectors } from '@klp/contractregistration/contractToRegistrationSlice';

const useStyles = makeStyles((theme) => ({
    D365: {
        maxWidth: '600px',
    },
    image: {
        '& > img': {
            width: '45%',
            minHeight: '1px', // IE11 hack
            maxWidth: '45%',
        },
    },
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
    selector: {
        width: '100%',
        minWidth: theme.spacing(25),
    },
    consultantSelectorWrapper: {
        maxWidth: '70%',
    },
}));

type Statuses =
    | RegistrationStatuses.REGISTRERING_D365
    | RegistrationStatuses.TIL_QA;

interface RegisterD365StepProps {
    handleCompleteStep: () => void;
    activity: IContractToRegistrationProcessActivity;
    status: {
        [key in Statuses]: string;
    };
    prosessInstansId: string;
}

export default function RegisterD365Step({
    handleCompleteStep,
    status,
    activity,
    prosessInstansId,
}: RegisterD365StepProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const { formFieldProcessing, formFieldError } = useSelector(
        contractregistrationSelectors,
    );
    const { kontraktId } = useParams<{
        kontraktId: string;
        lang: string;
    }>();
    const {
        data: contractToRegistration,
        isError: contractToRegistrationError,
    } = useGetContractToRegistrationQuery({
        kontraktId,
    });

    const { data: directLinkData, isError: directLinkError } =
        useGetDirectLinkQuery({
            dokumentId: kontraktId,
        });

    const { isUserCandidateForActivity } =
        useMapUserRolesToCandidateGroup(activity);

    const formFieldIsRequired = handleRequiredFormFields(activity?.formFields);

    const baseIsDisabled =
        activity.id === null ||
        activity.utfortDato !== null ||
        !isUserCandidateForActivity;

    const submitIsDisabled =
        baseIsDisabled ||
        formFieldProcessing ||
        formFieldError ||
        formFieldIsRequired;

    const newTabParams: ILevendeKontrakterParams = {
        autologin: true,
        hiddenNavbar: false,
    };

    const url = getRequiredConfig('WEBAPP_CONTRACT_FRAME_URL')
        .concat(`contract/${contractToRegistration?.lekoId}`)
        .concat(generateRouteParams(newTabParams));

    const handleOpenContract = () => {
        window.open(url, 'Contracts');
    };

    return (
        <>
            <Grid container={true} direction="row" justifyContent="flex-start">
                <Grid item={true}>
                    <Block
                        paddingLeft="compact"
                        paddingBottom="tiny"
                        paddingTop="compact"
                    >
                        <Heading
                            klpHeadingLevel="THREE"
                            klpColor="dark"
                            translation={`checkOffContract.step.${activity?.taskDefinitionId}.secondaryTitle`}
                        />
                    </Block>
                </Grid>
                <Grid item={true}>
                    <Block paddingLeft="compact" paddingTop="compact">
                        <ActionButton
                            actionType="external"
                            translation={`checkOffContract.step.${activity?.taskDefinitionId}.D365Button`}
                            onClick={() =>
                                window.open(getDynamics365URL(), '_blank')
                            }
                        />
                    </Block>
                </Grid>
            </Grid>
            <Block
                paddingLeft="compact"
                paddingTop="tiny"
                paddingBottom="compact"
                className={classes.D365}
            >
                {directLinkData?.kundeLenke && (
                    <Typography
                        display="inline"
                        translation={`checkOffContract.step.${activity?.taskDefinitionId}.levendeKontrakterDescription`}
                        translationOptions={{
                            kundeNavn: contractToRegistration?.kundeNavn,
                            kundeNummer: contractToRegistration?.kundeNummer,
                        }}
                    />
                )}{' '}
                <Typography
                    display="inline"
                    translation={`checkOffContract.step.${activity?.taskDefinitionId}.description`}
                    translationOptions={{
                        kundeNavn: contractToRegistration?.kundeNavn,
                        kundeNummer: contractToRegistration?.kundeNummer,
                    }}
                />
            </Block>
            {directLinkData?.kundeLenke && (
                <Block
                    paddingLeft="compact"
                    paddingTop="tiny"
                    paddingBottom="compact"
                >
                    <Grid
                        container={true}
                        direction="row"
                        justifyContent="flex-start"
                    >
                        <>
                            <Grid item={true}>
                                <Block>
                                    {t(
                                        `checkOffContract.step.${activity?.taskDefinitionId}.goToCustomer`,
                                    )}
                                </Block>
                            </Grid>
                            <Grid item={true}>
                                <Block paddingLeft="tiny">
                                    <Link
                                        klpTypographyVariant="small"
                                        klpColor="main"
                                        disabled={directLinkError}
                                        onClick={() =>
                                            directLinkData?.kundeLenke &&
                                            window.open(
                                                directLinkData.kundeLenke,
                                                '_blank',
                                            )
                                        }
                                    >
                                        {' '}
                                        <Typography>
                                            {' '}
                                            {directLinkData?.kundeNavn} (
                                            {directLinkData?.kundeNummer})
                                        </Typography>
                                    </Link>
                                </Block>
                            </Grid>
                        </>
                    </Grid>
                </Block>
            )}{' '}
            {directLinkData?.kontraktLenke && (
                <Block
                    paddingLeft="compact"
                    paddingTop="tiny"
                    paddingBottom="tiny"
                >
                    <Grid
                        container={true}
                        direction="row"
                        justifyContent="flex-start"
                    >
                        <>
                            <Grid item={true}>
                                <Block>
                                    {t(
                                        `checkOffContract.step.${activity?.taskDefinitionId}.goToContractDraft`,
                                    )}
                                </Block>
                            </Grid>
                            <Grid item={true}>
                                <Block paddingTop="none" paddingLeft="tiny">
                                    <Link
                                        klpTypographyVariant="small"
                                        klpColor="main"
                                        disabled={directLinkError}
                                        onClick={() =>
                                            directLinkData?.kontraktLenke &&
                                            window.open(
                                                directLinkData.kontraktLenke,
                                                '_blank',
                                            )
                                        }
                                    >
                                        {' '}
                                        <Typography>
                                            {directLinkData?.kontraktNummer}
                                        </Typography>
                                    </Link>
                                </Block>
                            </Grid>{' '}
                        </>
                    </Grid>
                </Block>
            )}
            {contractToRegistration?.lekoId && (
                <Block
                    paddingLeft="compact"
                    paddingTop="tiny"
                    paddingBottom="tiny"
                >
                    <Grid
                        container={true}
                        direction="row"
                        justifyContent="flex-start"
                    >
                        <>
                            <Grid item={true}>
                                <Block>
                                    {t(
                                        `checkOffContract.step.${activity?.taskDefinitionId}.goToLevendeKontrakter`,
                                    )}
                                </Block>
                            </Grid>
                            <Grid item={true}>
                                <Block paddingTop="none" paddingLeft="tiny">
                                    <Link
                                        klpTypographyVariant="small"
                                        klpColor="main"
                                        disabled={contractToRegistrationError}
                                        onClick={() =>
                                            contractToRegistration?.lekoId &&
                                            handleOpenContract()
                                        }
                                    >
                                        {' '}
                                        <Typography>
                                            {contractToRegistration?.lekoId}
                                        </Typography>
                                    </Link>
                                </Block>
                            </Grid>{' '}
                        </>
                    </Grid>
                </Block>
            )}
            {activity.formFields && (
                <Block paddingLeft="compact" paddingBottom="tiny">
                    <FormContainerKontraktsforvaltning
                        activity={activity}
                        prosessInstansId={prosessInstansId}
                        isDisabled={baseIsDisabled}
                    />
                </Block>
            )}
            <Block
                className={classes.consultantSelectorWrapper}
                paddingLeft="compact"
                paddingBottom="tiny"
                paddingTop="compact"
            >
                <Block>
                    <Heading
                        klpHeadingLevel="FOUR"
                        klpColor="dark"
                        translation={`checkOffContract.step.${activity?.taskDefinitionId}.selectController`}
                    />
                </Block>
                <ConsultantSelector
                    status={
                        status && {
                            RegistreringD365: status.TilQA,
                        }
                    }
                    activity={
                        activity && {
                            [Activities.UTFOR_REGISTRERING_D365]: activity,
                        }
                    }
                    filter="økonomi"
                    completeTask={true}
                    completeStepCallback={handleCompleteStep}
                    isDisabled={baseIsDisabled}
                    submitIsDisabled={submitIsDisabled}
                    sortBy={ConsultantRoles.QA_OKONOMI}
                    showDialogConfirmationForStep={true}
                />
            </Block>
        </>
    );
}
